import { useRef } from "react";

/** Structural equality for plain data; functions and other non-plain values compare by identity. */
function sameShape(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  return ka.every(
    (k) =>
      Object.hasOwn(b, k) &&
      sameShape((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]),
  );
}

/**
 * `value`, but the SAME reference for as long as its content is unchanged (compared structurally:
 * plain objects and arrays by content, functions and everything else by identity). A derived object
 * rebuilt on every render (a launcher peek, say) would otherwise re-run every effect that lists it
 * as a dependency. Meant for small plain data; a callback field that is re-created each render
 * defeats the stabilising, so wrap such callbacks (useStableCallback) first.
 */
export function useStableValue<T>(value: T): T {
  const ref = useRef<T | undefined>(undefined);
  const seen = useRef(false);
  if (!seen.current || !sameShape(ref.current, value)) {
    ref.current = value;
    seen.current = true;
  }
  return ref.current as T;
}
